import * as React from 'react';
import * as moment from 'moment';

import Accordion from 'src/components/common/AccordionTable';
import TablePaginator from 'src/components/common/TablePagination';
import OrdersTableSectionExpandable from '../OrdersTableSectionExpandable';
import EmptyOrders from '../EmptyOrders';
import OrderLineModel from 'src/model/orderline.model';
import Modal from 'src/components/common/Modal';
import CancelOrderModal from 'src/components/MyOrders/components/CancelOrderModal';
import CancelOrderModalMobile from 'src/components/MyOrders/components/CancelOrderModalMobile';
import PartReturnModel from 'src/model/partReturn.model';
import { IOrders } from 'src/model/modules/orders.model';
import { isMobile } from 'src/utils/responsiveHelpers';
import InvoiceService from 'src/services/invoice/InvoiceService';
import LocaleFormating from 'src/translation/localeFormating';
import { downloadFile } from 'src/utils/downloadHelper';
import './OrdersTable.scss';
import i18n from 'src/translation/i18n';
import Paginator from 'src/components/common/Paginator';
import { Alert } from '@material-ui/lab';
import { IconButton, Tooltip } from '@material-ui/core';
import MatrialCloseIcon from '@material-ui/icons/Close';
import CustomPaginator from 'src/components/common/CustomPaginator';
import Loader from 'src/components/common/Loader';

interface IOrdersTableState {
  showModal: boolean;
  selectedInvoice: OrderLineModel[] | PartReturnModel;
  orderStatus: string;
  showModalClicked: boolean;
  poNumber: string;
  date: string;
  isMobile: boolean;
  hideShowPdfError: boolean;
  loaderStatus: boolean;
}

interface IOrdersTableProps {
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  orders: IOrders[];
  sortOrder: 'ASC' | 'DESC' | 'AUTO';
  sortBy: string;
  searchResults: boolean; // Saves if the results where made from a search
  startExpanded: boolean;
  handlePagination: (event: any) => void;
  handleSortBy: (option: 'orderDate' | 'status') => void;
  handleGoForwardPage: () => void;
  handleGoBackPage: () => void;
  expandAllChild: (option: boolean) => void;
  onSubmitCancellation: (order: OrderLineModel[] | PartReturnModel) => void;
  t?: any;
  handlePageSizeChange?: (pageSize: number) => void;
  handleMobilePagination?: (type: 'next' | 'previous') => void;
}

const TableHeader = (props: {
  sortOrder: 'ASC' | 'DESC' | 'AUTO';
  sortBy: string;
  handleSortBy: (option: string) => void;
}) => {
  return (
    <tr className="rp-table-header">
      <td className="rp-table-header--name">
        {i18n.t('myOrders.searchByKeys.orderNumber')}{' '}
      </td>
      <td
        className={
          i18n.language === 'fr-CA'
            ? 'rp-table-header--confirmation-fr'
            : 'rp-table-header--confirmation'
        }
      >
        {i18n.t('myOrders.searchByKeys.confirmationNumber', {
          interpolation: { escapeValue: false }
        })}
      </td>
      <td className="rp-table-header--date clickable">
        {i18n.t('myOrders.orderDate')}
        <div
          onClick={e => props.handleSortBy('orderDate')}
          className="rp-table-header--clickable-arrows"
        >
          <span
            className={`rpds-arrow-drop-up ${props.sortBy === 'orderDate' &&
              props.sortOrder === 'ASC' &&
              'selected'}`}
          />
          <span
            className={`rpds-arrow-drop-down ${props.sortBy === 'orderDate' &&
              props.sortOrder === 'DESC' &&
              'selected'}`}
          />
        </div>
      </td>
      <td className="rp-table-header--warranty">
        {i18n.t('myOrders.searchByKeys.poNumber')}/
        {i18n.language.includes('en') ? ' ' : <br />}
        {i18n.t('myOrders.WarrantyNumber', {
          interpolation: { escapeValue: false }
        })}
        .
      </td>
      <td
        colSpan={2}
        className="rp-table-header--status clickable"
        onClick={e => props.handleSortBy('status')}
      >
        {i18n.t('myOrders.status')}
        <div className="rp-table-header--clickable-arrows">
          <span
            className={`rpds-arrow-drop-up ${props.sortBy === 'status' &&
              props.sortOrder === 'ASC' &&
              'selected'}`}
          />
          <span
            className={`rpds-arrow-drop-down ${props.sortBy === 'status' &&
              props.sortOrder === 'DESC' &&
              'selected'}`}
          />
        </div>
      </td>
    </tr>
  );
};

const mapCancellationstatus = (status: number) => {
  switch (status) {
    case 0:
      return {
        label: `${i18n.t('myOrders.cancelOrder')}`,
        subClass: 'cancellable'
      };
    case 1:
      return {
        label: `${i18n.t('myOrders.cancelRequested')}`,
        subClass: 'cancel-requested'
      };
    case 2:
      return {
        label: `${i18n.t('myOrders.partiallyCancelled')}`,
        subClass: 'partially-canceled'
      };
    case 3:
      return {
        label: `${i18n.t('myOrders.cancelled')}`,
        subClass: 'cancelled'
      };
    case 4:
      return {
        label: `${i18n.t('myOrders.cancelletionUnSuccess')}`,
        subClass: 'cancellation-unsuccesful'
      };
    default:
      return { label: '', subClass: '' };
  }
};

const getTranslatedStatus = (rawTxt: string) => {
  switch (rawTxt?.toLowerCase()) {
    case 'backordered':
      return i18n.t('myOrders.statusKeys.backordered');
    case 'order in process':
      return i18n.t('myOrders.statusKeys.orderInProcess');
    case 'invoiced':
      return i18n.t('myOrders.statusKeys.invoiced');
    case 'shipment pending':
      return i18n.t('myOrders.statusKeys.shipmentPending');
    default:
      return rawTxt;
  }
};

const SectionHeader = (props: {
  number: number;
  date: string;
  warranty: string;
  status: string;
  cancellationStatus: number;
  confirmation: string;
  invoices: { [key: string]: OrderLineModel[] };
  handleModalClicked: (
    invoices: { [key: string]: OrderLineModel[] },
    status: string,
    poNumber: string,
    date: string
  ) => void;
}) => {
  return (
    <React.Fragment>
      <td className="rp-section-header--name">
        <label className="rp-section-header--mobile-title">
          {i18n.t('myOrders.mobile.orderNo')}.{' '}
        </label>
        {props.number}
      </td>
      <td className="rp-section-header--confirmation">
        <label className="rp-section-header--mobile-title">
          {i18n.t('myOrders.mobile.confirmationNo')}
        </label>
        {props.confirmation}
      </td>
      <td className="rp-section-header--date">
        <label className="rp-section-header--mobile-title">
          {i18n.t('myOrders.mobile.orderDate')}
        </label>
        {moment(props.date).format('MM/DD/YY')}
      </td>
      <td className="rp-section-header--warranty">
        <label className="rp-section-header--mobile-title">
          {i18n.t('myOrders.mobile.poNo')}
        </label>
        {props.warranty}
      </td>
      <td className="rp-section-header--status">
        <label className="rp-section-header--mobile-title">
          {i18n.t('myOrders.status')}
        </label>
        {/* {props.status} */}
        {getTranslatedStatus(props.status)}
      </td>

      <td role="button" tabIndex={0}
        className={`rp-section-header--cancel-order ${
          mapCancellationstatus(props.cancellationStatus).subClass
        } ${
          props.status !== 'Backordered' &&
          props.status !== 'Order Cancelled' &&
          props.status !== 'Order Received'
            ? 'rp-hide-cancellation'
            : ''
        }`}

        onKeyDown={(event:any) => {
   	
          if(event.keyCode === 13) {     
            if (props.cancellationStatus === 0) {    
              props.handleModalClicked(    
                props.invoices,    
                props.status,    
                props.warranty,    
                moment(props.date).format('MM/DD/YY')    
              );    
            } else {    
              return;    
            }    
          }       
         }}
         
        onClick={() => {
          if (props.cancellationStatus === 0) {
            props.handleModalClicked(
              props.invoices,
              props.status,
              props.warranty,
              moment(props.date).format('MM/DD/YY')
            );
          } else {
            return;
          }
        }}
      >
        {mapCancellationstatus(props.cancellationStatus).label}
      </td>

      <div
        className={`rp-section-header--cancel-order-mobile ${
          mapCancellationstatus(props.cancellationStatus).subClass
        } ${
          props.status !== 'Backordered' &&
          props.status !== 'Order Cancelled' &&
          props.status !== 'Order Received'
            ? 'rp-hide-cancellation'
            : ''
        }`}
        onClick={() => {
          if (props.cancellationStatus === 0) {
            props.handleModalClicked(
              props.invoices,
              props.status,
              props.warranty,
              moment(props.date).format('MM/DD/YY')
            );
          } else {
            return;
          }
        }}
      >
        {mapCancellationstatus(props.cancellationStatus).label}
      </div>
    </React.Fragment>
  );
};

const Section = (props: {
  identifier: number;
  header: any;
  children?: any;
}) => {
  return <div>{props.children}</div>;
};

class OrdersTable extends React.Component<
  IOrdersTableProps,
  IOrdersTableState
> {
  constructor(props: IOrdersTableProps) {
    super(props);

    this.state = {
      showModal: false,
      selectedInvoice: null,
      orderStatus: '',
      showModalClicked: false,
      poNumber: '',
      date: '',
      isMobile: isMobile(),
      hideShowPdfError: false,
      loaderStatus: false
    };
  }

  public t: any;

  private handleShowHideModal = () => {
    this.setState({
      showModal: false,
      showModalClicked: false
    });
  };

  private handleModalClicked = (
    invoices: { [key: string]: OrderLineModel[] },
    status: string,
    poNumber: string,
    date: string,
    part: PartReturnModel = null
  ) => {
    if (Object.keys(invoices).length !== 0) {
      Object.keys(invoices).map(key => {
        this.renderModal(invoices[key], status, poNumber, date);
      });
    } else if (part) {
      this.renderModal(part, status, poNumber, date);
    }
    this.setState({ showModalClicked: true });
  };

  private setModalData = () => {
    const { selectedInvoice, orderStatus: status, poNumber, date } = this.state;
    return this.state.isMobile ? (
      <CancelOrderModalMobile
        order={selectedInvoice}
        poNumber={poNumber}
        date={date}
        showSlideMenu={
          this.state.showModal &&
          this.state.showModalClicked &&
          status === 'Backordered'
        }
        onCloseSlideMenu={this.handleShowHideModal}
        onSubmitCancellation={this.props.onSubmitCancellation}
      />
    ) : (
      this.state.showModal &&
        this.state.showModalClicked &&
        status === 'Backordered' && (
          <Modal handleCloseModal={this.handleShowHideModal}>
            <CancelOrderModal
              order={selectedInvoice}
              poNumber={poNumber}
              date={date}
              handleShowHideModal={this.handleShowHideModal}
              onSubmitCancellation={this.props.onSubmitCancellation}
            />
          </Modal>
        )
    );
  };

  public handleWindowSizeChange = () => {
    const isMob = isMobile();
    if (isMob !== this.state.isMobile) {
      this.setState({ isMobile: isMob });
    }
  };

  public componentWillMount() {
    window.addEventListener('resize', this.handleWindowSizeChange);
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowSizeChange);
  }

  private renderModal = (
    selectedInvoice: OrderLineModel[] | PartReturnModel,
    status: string,
    poNumber: string,
    date: string
  ) => {
    if (status === 'Backordered') {
      this.setState({
        selectedInvoice,
        orderStatus: status,
        poNumber,
        date,
        showModal: true
      });
    } else {
      this.setState({ selectedInvoice, orderStatus: status, showModal: false });
    }
  };

  public onPageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    console.log(e);
  };

  /* 
    getInvoicePreview
   */
  private getInvoicePreview = (invoiceNumber: string, orderNumber: string) => {
    this.setState({
      loaderStatus: true
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    InvoiceService.downloadInvoiceByOrderno(invoiceNumber, orderNumber)
      .then((response: any) => {
        downloadFile(
          response.data,
          `invoice-${invoiceNumber}-${LocaleFormating.getNowDate('short')}.pdf`
        );
        this.setState({
          loaderStatus: false
        });
      })
      .catch(error => {
        console.log(error);
        this.setState({
          hideShowPdfError: true,
          loaderStatus: false
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
  };

  public render() {
    this.t = this.props.t;
    return (
      <React.Fragment>
        {this.setModalData()}
        {this.state.loaderStatus ? <Loader /> : ''}
        <div className="rp-product-orders--paginator">
          <CustomPaginator
            activePage={this.props.pageNumber ? this.props.pageNumber + 1 : 1}
            totalPages={this.props.totalPages}
            itemsPerPage={this.props.pageSize}
            onPageChanged={this.props.handlePagination}
            totalItems={this.props.totalPages * this.props.pageSize}
            showPageInfo={true}
            onPageSizeChange={this.props.handlePageSizeChange}
            showPageRangeChange={true}
            currentPageSize={this.props.pageSize}
          />
        </div>
        {this.state.hideShowPdfError ? (
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  this.setState({
                    hideShowPdfError: false
                  });
                }}
              >
                <MatrialCloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity="error"
          >
            <div>
              <div className="error-message-to-show">
                {i18n.t('invoices.downloaderror')}
              </div>
            </div>
          </Alert>
        ) : (
          ''
        )}
        <div className="rp-my-orders-table">
          <div className="rp-product-orders-mobile-paginator">
            <TablePaginator
              handlePagination={this.props.handleMobilePagination}
              pagination={{
                pageNumber: this.props.pageNumber,
                pageSize: this.props.pageSize,
                totalPages: this.props.totalPages
              }}
            />
          </div>
          <Accordion
            showHandleCollapse={true}
            allowMultipleOpen={true}
            startExpanded={this.props.startExpanded}
            expandAllChild={this.props.expandAllChild}
            header={
              <TableHeader
                sortOrder={this.props.sortOrder}
                sortBy={this.props.sortBy}
                handleSortBy={this.props.handleSortBy}
              />
            }
          >
            {this.props.orders &&
              this.props.orders.map(order => {
                return (
                  <Section
                    identifier={order.orderNumber}
                    header={
                      <SectionHeader
                        number={order.orderNumber}
                        date={order.orderDate}
                        warranty={order.poNumber}
                        status={order.status}
                        cancellationStatus={order.cancellationStatus}
                        confirmation={order.confimation}
                        handleModalClicked={this.handleModalClicked}
                        invoices={order.invoices}
                      />
                    }
                    key={order.orderNumber}
                  >
                    <OrdersTableSectionExpandable
                      invoices={order.invoices}
                      order={order}
                      loaderStatus={this.state.loaderStatus}
                      handleModalClicked={this.handleModalClicked}
                      getInvoicePreview={this.getInvoicePreview}
                    />
                  </Section>
                );
              })}
          </Accordion>
          {this.props.orders && this.props.orders.length === 0 && (
            <EmptyOrders noResults={this.props.searchResults} />
          )}
          <div className="rp-product-orders-mobile-paginator">
            <TablePaginator
              handlePagination={this.props.handleMobilePagination}
              pagination={{
                pageNumber: this.props.pageNumber,
                pageSize: this.props.pageSize,
                totalPages: this.props.totalPages
              }}
            />
          </div>
        </div>
        <div className="rp-product-orders--paginator">
          <CustomPaginator
            activePage={this.props.pageNumber ? this.props.pageNumber + 1 : 1}
            totalPages={this.props.totalPages}
            itemsPerPage={this.props.pageSize}
            onPageChanged={this.props.handlePagination}
            totalItems={this.props.totalPages * this.props.pageSize}
            showPageInfo={true}
            onPageSizeChange={this.props.handlePageSizeChange}
            showPageRangeChange={true}
            currentPageSize={this.props.pageSize}
          />
        </div>
      </React.Fragment>
    );
  }
}
export default OrdersTable;
